<template>
  <Layout>
    <div class="w-md-50 px-3 px-md-0">
      <div class="d-flex justify-content-between mt-3">
        <div>
          <h1 class="my-auto">
            Stock Screener
          </h1>
        </div>
        <ColumnSettings
          :table-fields="columnSettingOptions"
          @filterChange="handleColumnSettings"
          @precisionChange="handlePrecisionChange"
        />
      </div>
      <b-row class="mt-4">
        <div>
          <b-table
            key="symbol"
            ref="stock-table"
            class="datatables-custom text-center align-middle"
            :items="stocks"
            :fields="filteredColumns"
            responsive
            bordered
            hover
            stacked="sm"
            :busy="isLoading"
            show-empty
          >
            <template v-slot:cell(symbol)="row">
              <div class="d-flex">
                <div class="text-center bg-dark p-2">
                  <img
                    v-if="!noImage[row.item.symbol]"
                    :src="`https://financialmodelingprep.com/image-stock/${row.item.symbol}.png`"
                    style="width: 30px; height: 30px;"
                    @error="handleErrorImage(row.item.symbol)"
                  >
                  <div
                    v-if="noImage[row.item.symbol]"
                    class="d-flex"
                    style="width: 30px; height: 30px;"
                  >
                    <div class="m-auto text-white fw-bold fs-5">
                      {{ row.item.symbol.substring(0, 2) }}
                    </div>
                  </div>
                </div>
                <div class="ms-3 my-auto">
                  {{ row.item.symbol }}
                </div>
              </div>
            </template>
            <template v-slot:cell(market_cap)="row">
              {{ row.item.market_cap | billionFilter }}
            </template>
            <template v-slot:cell()="item">
              <div
                v-if="item.field.isPercentage"
                :class="(item.value < 0 || Number.isNaN(Number.parseFloat(item.value))) ? 'text-danger' : ''"
              >
                {{ item.value | percentageFilter }}
              </div>
              <div
                v-else-if="item.field.isRatio"
                :class="(item.value < 0 || Number.isNaN(Number.parseFloat(item.value))) ? 'text-danger' : ''"
              >
                {{ item.value | ratioFilter }}
              </div>
              <div v-else>
                {{ item.value }}
              </div>
            </template>
          </b-table>

          <b-row class="d-md-flex mb-3 justify-content-between">
            <b-col
              md="5"
              lg="4"
              xl="3"
            >
              <div class="d-flex mb-3 mb-md-0">
                <span class="fw-bold me-auto my-auto">
                  Items Per Page
                </span>
                <div class="ms-auto">
                  <label
                    v-for="(option, index) in [20, 30, 50]"
                    :key="index"
                    class="my-auto ms-2"
                  >
                    <input
                      v-model="perPage"
                      type="radio"
                      :value="option"
                      checked
                    >
                    <span>{{ option }}</span>
                  </label>
                </div>
              </div>
            </b-col>
            <b-col
              md="7"
              lg="8"
              xl="9"
              class="d-flex justify-content-between"
            >
              <div class="ms-0 mx-md-auto my-auto fw-bold">
                Page {{ currentPage }} of {{ totalPage }}
              </div>
              <b-pagination
                v-model="currentPage"
                class="mb-0"
                :total-rows="totalRows"
                :per-page="perPage"
                size="md"
                align="right"
              />
            </b-col>
          </b-row>
        </div>
      </b-row>
    </div>
  </Layout>
</template>

<script>
import { mapState } from 'vuex';
import appConfig from '@/app.config';
import Layout from '@/layouts/main';
import ColumnSettings from '@/components/ColumnSettings.vue';

import { stockParams } from '@/utils/constant';
import { PrecisionService } from '../services/local-storage';

export default {
  page: {
    title: 'Home',
    meta: [
      {
        name: 'description',
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    ColumnSettings,
  },
  filters: {
    billionFilter(value) {
      return `${(value / 1000000000).toFixed(2)} B`;
    },
    percentageFilter(value) {
      const precision = PrecisionService.get();
      const number = Number.parseFloat(value);
      if (Number.isNaN(number)) {
        return 'NaN';
      }
      const percentage = (number * 100).toFixed(precision);
      return `${percentage}%`;
    },
    ratioFilter(value) {
      const precision = PrecisionService.get();
      const number = Number.parseFloat(value);
      if (Number.isNaN(number)) {
        return 'NaN';
      }
      return number.toFixed(precision);
    },
  },
  data() {
    return {
      stockParams,
      columnSettings: [],

      noImage: {},

      totalRows: 1,
    };
  },
  computed: {
    ...mapState('stock', {
      stocks: (state) => state.stocks,
      meta: (state) => state.meta,
      isLoading: (state) => state.isLoading,
    }),

    ...mapState('filter', {
      stateCurrentPage: (state) => state.currentPage,
      statePerPage: (state) => state.perPage,
    }),

    currentPage: {
      get() {
        return this.stateCurrentPage;
      },
      set(value) {
        this.$store.commit('filter/SET_PAGE', value);
      },
    },
    perPage: {
      get() {
        return this.statePerPage;
      },
      set(value) {
        this.$store.commit('filter/SET_PER_PAGE', value);
      },
    },

    filteredColumns() {
      const defaultColumn = [
        {
          key: 'symbol', label: 'Symbol', thClass: 'text-center align-middle ', thStyle: { minWidth: '6rem' },
        },
      ];
      const filteredColumn = this.stockParams.filter((item) => this.columnSettings.includes(item.key));
      return defaultColumn.concat(filteredColumn);
    },
    columnSettingOptions() {
      return this.stockParams.filter((item) => item.key != 'symbol');
    },
    totalPage() {
      return this.meta?.total_page ? this.meta?.total_page : 1;
    },
  },
  watch: {
    meta(meta) {
      this.totalRows = meta.total_row;
    },
  },
  methods: {
    handleColumnSettings(newSettings) {
      this.columnSettings = newSettings;
    },

    handleErrorImage(key) {
      this.noImage[key] = true;
      this.$refs['stock-table'].refresh();
    },

    handlePrecisionChange() {
      this.$refs['stock-table'].refresh();
    },
  },
};
</script>

<style>
label {
  display: inline-block;
  position: relative;
}

label input[type="radio"] {
  display: none;
}

label span {
  display: inline-block;
  padding: 8px 12px;
  border: 1px solid #ccc;
  cursor: pointer;
}

label input[type="radio"]:checked + span {
  font-weight: 1000;
  border-color: #556ee6;
}
</style>
